import { uuid } from '@shared/util/uuid';
import { MBUploadUtilFile } from './types';

const POSTER_CONTENT_TYPE = 'image/jpeg';
const POSTER_QUALITY = 0.8;
/** Far enough in to clear a black opening frame, near enough to still be the opening shot. */
const POSTER_SECONDS = 0.1;
/**
 * How long to wait for a frame before going without one.
 *
 * Decoding a file the browser already holds is fast, so this is not a budget — it is the only
 * thing that ends the wait when no event ever arrives. `seeked` can go missing on a partly
 * supported codec, on a stalled decode, or on a `currentTime` that lands where the video
 * already is, and the upload this sits inside would otherwise never finish.
 */
const POSTER_TIMEOUT_MS = 5000;

/**
 * A still from a video, taken before it is uploaded, while the file is still local.
 *
 * The alternative is asking a `<video>` for a frame after upload, which cannot be done cheaply:
 * painting a frame means seeking, and seeking makes the element buffer forward from that point —
 * in practice the whole file, every time the thumbnail renders. Capturing here costs one decode
 * of bytes the browser already holds.
 *
 * Returns null when the browser cannot decode the video, which is not worth failing an upload
 * over — the caller just goes without a poster.
 */
export async function captureVideoPoster(
  source: MBUploadUtilFile,
): Promise<MBUploadUtilFile | null> {
  const blob = await drawFirstFrame(source.url).catch(() => null);
  if (!blob) {
    return null;
  }

  return {
    id: uuid(),
    url: URL.createObjectURL(blob),
    name: `${source.name}.poster.jpg`,
    contentType: POSTER_CONTENT_TYPE,
    size: blob.size,
  };
}

function drawFirstFrame(objectUrl: string): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    // The source is a local object URL, so this decodes what is already in memory.
    video.preload = 'auto';
    video.src = objectUrl;

    // Settling detaches the handlers and drops the source, so a late event cannot fire and the
    // element stops holding the decoded video.
    let settled = false;
    const settle = (finish: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      video.onerror = null;
      video.onloadeddata = null;
      video.onseeked = null;
      video.removeAttribute('src');
      video.load();
      finish();
    };

    const succeed = (blob: Blob | null) => settle(() => resolve(blob));
    const fail = () => settle(() => reject(new Error('Could not decode video for poster')));

    const timeout = setTimeout(fail, POSTER_TIMEOUT_MS);

    video.onerror = fail;

    video.onloadeddata = () => {
      // Seeking is free here for the same reason: nothing is being fetched.
      video.currentTime = Math.min(POSTER_SECONDS, video.duration || POSTER_SECONDS);
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      if (!context || !canvas.width || !canvas.height) {
        fail();
        return;
      }

      // Drawn before settling: dropping the source first would leave nothing to paint.
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(succeed, POSTER_CONTENT_TYPE, POSTER_QUALITY);
    };
  });
}
